import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  FormatBold as FormatBoldIcon,
  FormatClear as FormatClearIcon
} from '@mui/icons-material';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

type Props = {
  value: string;
  disabled?: boolean;
  modified?: boolean;
  highlightBg?: string;
  editBorder?: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

// Block-level elements that mean the value already has paragraph structure.
const BLOCK_TAG_PATTERN = /<(p|div|ul|ol|li|h[1-6]|blockquote)\b/i;
const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/i;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Convert any stored value (plain text, inline HTML, or full block HTML) into
 * the block-level HTML that TipTap/ProseMirror expects as its initial content.
 */
function buildEditorContent(value: string): string {
  const s = String(value ?? '').trim();
  if (!s) return '';

  // Already has block structure (<p>, <ul>, etc.) — use as-is.
  if (BLOCK_TAG_PATTERN.test(s)) return s;

  // Has inline HTML but no block wrapper — wrap in <p> to match TipTap output.
  if (HTML_TAG_PATTERN.test(s)) return `<p>${s}</p>`;

  // Plain text — split on double newlines to create separate paragraphs.
  return s
    .split(/\n{2,}/)
    .map(para => `<p>${escapeHtml(para).replace(/\n/g, '<br />')}</p>`)
    .join('');
}

function getEditorHtml(editor: Editor): string {
  return editor.isEmpty ? '' : editor.getHTML();
}

export const RichTextInspectorField: React.FC<Props> = ({
  value,
  disabled = false,
  modified = false,
  highlightBg,
  editBorder,
  onChange,
  onFocus,
  onBlur
}) => {
  // isInitializedRef gates onUpdate so we never fire onChange during TipTap's
  // async initialization transactions (which would create false "modified" state).
  const isInitializedRef = React.useRef(false);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          code: false,
          codeBlock: false,
          horizontalRule: false,
          italic: false,
          strike: false,
          bulletList: false,
          orderedList: false,
          listItem: false
        })
      ],
      content: buildEditorContent(value),
      editable: !disabled,
      editorProps: {
        attributes: { spellcheck: 'false' }
      },
      onFocus: () => onFocus?.(),
      onBlur: () => onBlur?.(),
      onUpdate: ({ editor: e }) => {
        // Block any onUpdate that fires before the editor is considered
        // initialized. TipTap v3 fires deferred init transactions that look
        // like user edits — this flag prevents them from leaking as onChange.
        if (!isInitializedRef.current) return;
        onChange(getEditorHtml(e));
      }
    },
    []
  );

  // After the editor mounts, wait one macrotask (setTimeout 0) so that all of
  // TipTap's queued init transactions have settled before we start accepting
  // onUpdate calls as real user edits.
  React.useEffect(() => {
    if (!editor) return;
    isInitializedRef.current = false;
    const id = setTimeout(() => {
      isInitializedRef.current = true;
    }, 0);
    return () => {
      clearTimeout(id);
      isInitializedRef.current = false;
    };
  }, [editor]);

  // Sync disabled state.
  React.useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  // Sync incoming value prop changes (e.g. discard/reset from parent).
  // emitUpdate: false prevents this from triggering onUpdate.
  React.useEffect(() => {
    if (!editor) return;
    const next = buildEditorContent(value);
    const current = getEditorHtml(editor);
    if (current === next) return;
    editor.commands.setContent(next || '', { emitUpdate: false });
  }, [editor, value]);

  const runCommand = (command: (e: Editor) => void) => {
    if (!editor || disabled) return;
    command(editor);
  };

  const toolbarButtons = [
    {
      label: 'Bold',
      icon: <FormatBoldIcon fontSize="small" />,
      active: !!editor?.isActive('bold'),
      onClick: () => runCommand(e => e.chain().focus().toggleBold().run())
    },
    {
      label: 'Clear formatting',
      icon: <FormatClearIcon fontSize="small" />,
      active: false,
      onClick: () => runCommand(e => e.chain().focus().clearNodes().unsetAllMarks().run())
    }
  ];

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid',
        borderColor: modified ? editBorder || 'warning.main' : 'divider',
        borderWidth: modified ? '2px' : '1px',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: modified ? highlightBg : 'background.paper',
        ...(disabled && {
          bgcolor: 'action.disabledBackground',
          cursor: 'not-allowed'
        })
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          px: 0.5,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
          flexWrap: 'wrap'
        }}
      >
        {toolbarButtons.map((button) => (
          <Tooltip key={button.label} title={button.label}>
            <span>
              <IconButton
                size="small"
                disabled={disabled || !editor}
                onMouseDown={(event) => event.preventDefault()}
                onClick={button.onClick}
                sx={{
                  color: button.active ? 'primary.main' : 'text.secondary',
                  bgcolor: button.active ? 'action.selected' : 'transparent'
                }}
              >
                {button.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </Box>
      <Box
        onClick={() => {
          if (editor && !disabled) editor.chain().focus().run();
        }}
        sx={{
          px: 1.5,
          py: 1,
          minHeight: 72,
          cursor: disabled ? 'not-allowed' : 'text',
          '& .ProseMirror': {
            minHeight: 48,
            outline: 'none',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          },
          '& .ProseMirror p': { my: 0 },
          '& .ProseMirror p + p': { mt: 1 }
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextInspectorField;
